import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpOptions,
  InMemoryCache,
  Operation,
  createHttpLink,
  split,
} from "@apollo/client";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { onError } from "@apollo/client/link/error";
import { useEnvContext } from "context/EnvContext";
import { APP_PROVIDER_SEARCH, BACKEND } from "core/consts";
import transport from "core/model/api/transport";
import { getApp } from "core/model/config";
import { activateWebSocket, isProd } from "core/model/utils/featureFlags";
import { ConfigType } from "core/types";
import React, { useMemo } from "react";
import { useTracking } from "react-tracking";
import { useSelectToken } from "reduxentities/hooks";
import cryptoLinks from "../encryption";
import { operationTimesLinks } from "../utils/operationTimes";
import { WebSocketConfig } from "./config";
import { useGetWebSocketEventHandlers } from "./utils";
import { WebSocketLink } from "./WebSocketLink";

const EXCLUDED_ROUTES = [
  "/auth",
  "/wizard",
  "/activationwizard",
  "/loginwizard",
];

let authToken: string | undefined = undefined;
const setAuthToken = (token: string | undefined) => {
  authToken = token;
};
const getAuthToken = () => authToken;

type RestParams = {
  fragment: string;
  queryName: string;
  restMethod: string;
  restPathAndQuery: string;
};

type Variables = {
  [key: string]: unknown;
  _rest: RestParams;
};

type ApolloCustomFetch = NonNullable<HttpOptions["fetch"]>;

function getRoute(uri: URL | string | unknown): string | null {
  if (uri instanceof URL) {
    return uri.href;
  }

  if (typeof uri === "string") {
    return uri;
  }

  return null;
}

export const customFetch: ApolloCustomFetch = async (
  uri,
  apolloOptions: (RequestInit & { variables?: Variables }) | undefined,
) => {
  if (typeof apolloOptions?.body !== "string") {
    throw new Error("Expected apolloOptions.body to be a string.");
  }
  const body = JSON.parse(apolloOptions.body);
  // 'query' not currently used in the BE and roughly double the size
  // of the 'fragment' in the network payload
  // when transitioning to using 'query' for caching, look into
  // optimizing the string size
  body.query = undefined;

  body.variables = apolloOptions.variables;

  const route = getRoute(uri);
  if (route === null) {
    throw new Error("URI must be a string or URL.");
  }

  const response = await transport.post<Response>({
    body,
    options: {
      isApollo: true,
      routeName: "apollo",
    },
    route,
  });

  if (response == null) {
    throw new Error("Transport returned no response for Apollo request");
  }

  return response;
};

export function getOperationRestParams(operation: Operation): RestParams {
  const body = operation.query.loc?.source.body;
  const bodyRegex = /{([\s\S]*)}/;
  const [, queryBody] = body?.match(bodyRegex) ?? [];
  const fragmentRegex = /rest\([\s\S]*\)[\s\S]*?({[\s\S]*})/;
  const [, fragmentBody] = queryBody?.match(fragmentRegex) ?? [];
  const fragment = fragmentBody?.replace(/ /g, "");

  const restRegex = /@rest\(([\s\S]*)\)/;
  const [, restDef] = body?.match(restRegex) ?? [];

  const restDefObj = Object.fromEntries(
    restDef
      .replace(/\n/g, "%%")
      .replace(/",/g, "%%")
      .split("%%")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((p) => p.replace(/ /g, "").replace(/"/g, "").split(":")),
  );

  const operationNameRegex = /{([\s\S]*)@rest([\s\S]*)}/;
  const [, queryNameDef] = body?.match(operationNameRegex) ?? [];
  const queryName = String(queryNameDef)
    .replace(/ |\n/g, "")
    // ReDoS-safe: use non-greedy match or bounded quantifier instead of .*
    .replace(/\([^)]*\)/g, "");
  // const or = (operation as any).query.definitions[0].selectionSet.selections[0].name

  let restPathAndQuery = restDefObj?.path || null;
  if (restPathAndQuery && operation.variables) {
    Object.entries(operation.variables).forEach(([varName, varValue]) => {
      const placeholder = `{args.${varName}}`;
      const escapedPlaceholder = placeholder.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      restPathAndQuery = restPathAndQuery.replace(
        new RegExp(escapedPlaceholder, "g"),
        varValue,
      );
    });
  }

  const restMethod = restDefObj?.method || "GET";

  return {
    fragment,
    queryName,
    restMethod,
    restPathAndQuery,
  };
}

// setup error logging
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const app = getApp();
  if (graphQLErrors)
    graphQLErrors.map(({ locations, message, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    const isExcludedRoute = EXCLUDED_ROUTES.some((route) =>
      window.location.pathname.includes(route),
    );
    if (networkError.message?.includes("401") && !isExcludedRoute) {
      window.location.href = "/logout?reason=unauthorized";
    }

    // to do: extend to all apps
    if (app == APP_PROVIDER_SEARCH && networkError.message?.includes("429"))
      window.location.href = "/error?reason=429";
  }
});

function getWebSocketUri(config: ConfigType | null): string {
  const matchingBackend = config?.backend[BACKEND.MATCHING];
  if (matchingBackend?.host == null) {
    return "badconfig";
  }

  return `${matchingBackend.host}:${matchingBackend.port}`;
}

const ApolloClientProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { app, config, env } = useEnvContext();
  const { getTrackingData, trackEvent } = useTracking();
  const getWebSocketEventHandlers = useGetWebSocketEventHandlers();

  setAuthToken(useSelectToken());

  const wsLink = useMemo(() => {
    if (!activateWebSocket(app)) return null;

    return new WebSocketLink({
      connectionParams: () => {
        const token = getAuthToken();
        if (!token) {
          return {};
        }
        return {
          app: getApp(),
          Authorization: `Bearer ${token}`,
        };
      },
      keepAlive: WebSocketConfig.PING_REFRESH_RATE,
      lazy: true,
      on: getWebSocketEventHandlers(),
      retryAttempts: WebSocketConfig.RETRY_ATTEMPTS,
      url: `wss://${getWebSocketUri(config)}/websocket?apollo=true`,
    });
  }, [env, config]);

  const httpLink = useMemo(
    () =>
      createHttpLink({
        fetch: customFetch,
      }),
    [env],
  );

  const requestEndpointLink = new ApolloLink((operation, forward) => {
    operation.setContext(() => {
      const variables: Variables = {
        ...operation.variables,
        _rest: getOperationRestParams(operation),
      };
      return {
        fetchOptions: {
          variables,
        },
      };
    });
    return forward(operation);
  });

  const nameLink = new ApolloLink((operation, forward) => {
    operation.setContext(() => ({
      uri: `/graphql?${operation.operationName}`,
    }));
    return forward ? forward(operation) : null;
  });

  const splitLink = useMemo(
    () =>
      split(
        ({ query }) => {
          const definition = query.definitions[0];
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          );
        },
        wsLink ?? ApolloLink.from([]), // activateWebSocket
        ApolloLink.from([requestEndpointLink, nameLink, httpLink]),
      ),
    [env],
  );

  // setup client
  const client = useMemo(() => {
    // setup cache state
    const cache = new InMemoryCache({
      // see https://github.com/apollographql/apollo-client/issues/11808
      // Apollo Client 3.14.0 enforces #11808's "All-supertype" `merge: true`
      // strictly (PR #12750), which makes `mergeObjects` throw invariant #9
      // ("Cannot automatically merge arrays") when an array-valued field is
      // written under the wildcard policy. Older versions silently replaced
      // arrays. We restore that behavior explicitly: deep-merge plain objects,
      // but always replace arrays with the incoming value.
      possibleTypes: {
        All: [".*"],
      },
      typePolicies: {
        All: {
          merge(existing, incoming, { mergeObjects }) {
            if (Array.isArray(existing) || Array.isArray(incoming)) {
              return incoming;
            }
            return mergeObjects(existing, incoming);
          },
        },
      },
    });

    return new ApolloClient({
      cache,
      link: ApolloLink.from([
        ...operationTimesLinks(trackEvent),
        ...cryptoLinks(getTrackingData()),
        errorLink,
        splitLink,
      ]),
    });
  }, [env]);

  if (!isProd) {
    loadDevMessages();
    loadErrorMessages();
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloClientProvider;
